import { useMemo, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDateRange } from "@shared/hooks/useDateRange";
import { DateRangeFilter } from "@shared/ui/DateRangeFilter";
import { useDebouncedValue } from "@shared/hooks/useDebouncedValue";
import { Pagination } from "@shared/ui/Pagination";
import { useEvaluations, useDeleteEvaluation } from "@features/evaluation/queries";
import { useAuth } from "@features/auth/AuthContext";
import { ConfirmDialog } from "@shared/ui/ConfirmDialog";
import { exportToExcel } from "@shared/utils/excel-export";
import { exportEvaluationPdf } from "./evaluation-pdf";
import { EvaluationDetailDialog } from "./EvaluationDetailDialog";
import type { EvaluationEnvelope } from "@/types/evaluation";
import { HttpError } from "@shared/http/client";
import { today } from "@shared/utils/date";

const PAGE_SIZE = 50;

export function HistorialPage() {
  const navigate = useNavigate();
  const { range } = useDateRange();
  const { user, hasRole } = useAuth();
  const deleteMut = useDeleteEvaluation();
  const canEdit = hasRole("Administrador", "Evaluador");
  const [detail, setDetail] = useState<EvaluationEnvelope | null>(null);
  const [toDelete, setToDelete] = useState<EvaluationEnvelope | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("doc") ?? "");
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const [page, setPage] = useState(1);
  const [evaluadorFilter, setEvaluadorFilter] = useState("");
  const [resultadoFilter, setResultadoFilter] = useState("");
  const [decisionFilter, setDecisionFilter] = useState("");

  // Al entrar a /historial?doc=X desde el Dashboard, prellenar el buscador
  useEffect(() => {
    const doc = searchParams.get("doc");
    if (doc && doc !== search) {
      setSearch(doc);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Reset a página 1 cuando cambia cualquier filtro
  useEffect(() => { setPage(1); }, [debouncedSearch, range.from, range.to, evaluadorFilter, resultadoFilter, decisionFilter]);

  const { data, isLoading, error, isFetching } = useEvaluations({
    from: range.from || undefined,
    to: range.to || undefined,
    search: debouncedSearch || undefined,
    page,
    limit: PAGE_SIZE,
  });

  const canDelete = hasRole("Administrador");
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const uniqueEvaluadores = useMemo(() => {
    const set = new Set<string>();
    items.forEach((e) => { const v = String(e.evaluadorNombre || e.evaluador || "").trim(); if (v) set.add(v); });
    return Array.from(set).sort();
  }, [items]);
  const uniqueResultados = useMemo(() => {
    const set = new Set<string>();
    items.forEach((e) => { const v = String(e.resultadoClinico || "").trim(); if (v) set.add(v); });
    return Array.from(set).sort();
  }, [items]);
  const uniqueDecisiones = useMemo(() => {
    const set = new Set<string>();
    items.forEach((e) => { const v = String(e.accionFinal || "").trim(); if (v) set.add(v); });
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => items.filter((e) => {
    if (evaluadorFilter && String(e.evaluadorNombre || e.evaluador || "") !== evaluadorFilter) return false;
    if (resultadoFilter && String(e.resultadoClinico || "") !== resultadoFilter) return false;
    if (decisionFilter && String(e.accionFinal || "") !== decisionFilter) return false;
    return true;
  }), [items, evaluadorFilter, resultadoFilter, decisionFilter]);

  const activeExtraFilters = [evaluadorFilter, resultadoFilter, decisionFilter].filter(Boolean).length;

  const clearFilters = () => {
    setSearch(""); setEvaluadorFilter(""); setResultadoFilter(""); setDecisionFilter("");
  };

  const onExportExcel = () => {
    void exportToExcel(
      filtered,
      [
        { key: "doc",                  label: "Documento" },
        { key: "nombre",               label: "Nombre" },
        { key: "cirugia",              label: "Cirugía" },
        { key: "fecha",                label: "Fecha programada" },
        { key: "fechaRegistro",        label: "Fecha registro" },
        { key: "evaluadorNombre",      label: "Evaluador" },
        { key: "estadoAdministrativo", label: "Estado administrativo" },
        { key: "resultadoClinico",     label: "Resultado clínico" },
        { key: "accionFinal",          label: "Decisión final" },
      ],
      `historial-${today()}`,
      {
        reportTitle: "FOCA — Historial de valoraciones (página actual)",
        rangeFrom: range.from,
        rangeTo: range.to,
        generatedBy: user?.name || user?.username || "",
      }
    );
  };

  return (
    <div className="p-24">
      <div className="page-header">
        <h1>
          Historial de valoraciones
          {total > 0 && <span className="badge badge-blue ml-8">{total}</span>}
        </h1>
        <p>Todas las valoraciones registradas con filtros por fecha, evaluador, resultado y decisión.</p>
      </div>

      <div className="mb-16">
        <DateRangeFilter />
      </div>

      <div className="filter-row mt-16">
        <input
          type="text"
          placeholder="Buscar por nombre, documento o cirugía…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="filter-search"
        />
        <select value={evaluadorFilter} onChange={(e) => setEvaluadorFilter(e.target.value)} className="filter-select">
          <option value="">Todos los evaluadores</option>
          {uniqueEvaluadores.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={resultadoFilter} onChange={(e) => setResultadoFilter(e.target.value)} className="filter-select">
          <option value="">Todos los resultados</option>
          {uniqueResultados.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={decisionFilter} onChange={(e) => setDecisionFilter(e.target.value)} className="filter-select">
          <option value="">Todas las decisiones</option>
          {uniqueDecisiones.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        {(activeExtraFilters > 0 || debouncedSearch) && (
          <button type="button" className="btn btn-sm btn-secondary" onClick={clearFilters}>
            Limpiar
          </button>
        )}
        <button
          type="button"
          className="btn btn-sm btn-secondary"
          onClick={onExportExcel}
          disabled={filtered.length === 0}
        >
          📥 Exportar Excel ({filtered.length})
        </button>
      </div>

      {error instanceof HttpError && (
        <div className="alert-inline alert-inline--danger mt-16">Error cargando historial: {error.message}</div>
      )}
      {(isLoading || isFetching) && <div className="text-muted mt-16">{isLoading ? "Cargando…" : "Actualizando…"}</div>}

      {!isLoading && filtered.length === 0 && (
        <div className="text-muted mt-24">
          {total === 0 ? "No hay valoraciones en el rango o búsqueda actual." : "Sin resultados con los filtros de columna aplicados."}
        </div>
      )}

      {filtered.length > 0 && (
        <>
          <div className="table-scroll mt-16">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Documento</th><th>Paciente</th><th>Procedimiento</th><th>Fecha Cx</th>
                  <th>Programó</th><th>Evaluó</th>
                  <th>Estado Adm.</th><th>Resultado Clínico</th><th>Decisión Final</th>
                  <th style={{ textAlign: "right" }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={e.id}>
                    <td>{e.doc}</td>
                    <td>{e.nombre}</td>
                    <td>{e.cirugia}</td>
                    <td>{e.fecha || e.fechaRegistro}</td>
                    <td>{e.programador}</td>
                    <td>{e.evaluadorNombre || e.evaluador}</td>
                    <td>{e.estadoAdministrativo}</td>
                    <td>{e.resultadoClinico}</td>
                    <td>{e.accionFinal}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" className="btn btn-sm btn-secondary" onClick={() => setDetail(e)}>Ver</button>
                      {canEdit && (
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary ml-8"
                          onClick={() => navigate(`/nueva?id=${e.id}&edit=1`)}
                        >
                          Editar
                        </button>
                      )}
                      <button type="button" className="btn btn-sm btn-secondary ml-8" onClick={() => void exportEvaluationPdf(e)}>PDF</button>
                      {canDelete && (
                        <button type="button" className="btn btn-sm btn-danger ml-8" onClick={() => setToDelete(e)}>Eliminar</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} total={total} limit={PAGE_SIZE} onPageChange={setPage} />
        </>
      )}

      <EvaluationDetailDialog evaluation={detail} onClose={() => setDetail(null)} />

      <ConfirmDialog
        open={!!toDelete}
        title="Eliminar valoración"
        message={
          toDelete
            ? `Se eliminará la valoración de ${toDelete.nombre || toDelete.doc} (id ${toDelete.id}). Queda registrada en auditoría con tu usuario. Esta acción es irreversible.`
            : ""
        }
        confirmLabel={deleteMut.isPending ? "Eliminando…" : "Eliminar"}
        tone="danger"
        onCancel={() => setToDelete(null)}
        onConfirm={() => {
          if (!toDelete?.id) return;
          deleteMut.mutate(toDelete.id, { onSettled: () => setToDelete(null) });
        }}
      />
    </div>
  );
}
